/**
 * "You may also like" slider for Dawn's related-products section.
 * Uses the Swiper 11 library the theme already loads (theme.liquid) — nothing extra is downloaded.
 *
 * <product-recommendations> fetches its products after page load and replaces its innerHTML.
 * Because this is a custom element, each newly inserted slider initialises itself on connect,
 * and destroys itself when removed (Theme Editor re-render, AJAX refresh). A data flag prevents
 * double initialisation.
 */
(() => {
  if (customElements.get('related-products-slider')) return;

  const whenSwiper = () =>
    new Promise((resolve) => {
      const check = () => (typeof window.Swiper === 'function' ? resolve(window.Swiper) : window.setTimeout(check, 50));
      check();
    });

  class RelatedProductsSlider extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      whenSwiper().then(() => {
        if (this.isConnected && this.dataset.initialized === 'true') this.init();
      });
    }

    disconnectedCallback() {
      if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(true, true);
      this.swiper = null;
      this.dataset.initialized = 'false';
    }

    num(name, fallback) {
      const value = parseFloat(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    init() {
      const container = this.querySelector('.related-slider__container');
      if (!container || !container.querySelector('.related-slider__slide')) return;

      const pagination = this.dataset.pagination;
      const paginationEl = this.querySelector('[data-rps-pagination]');

      this.classList.add('is-ready');

      this.swiper = new window.Swiper(container, {
        containerModifierClass: 'rps-swiper-',
        wrapperClass: 'related-slider__wrapper',
        slideClass: 'related-slider__slide',
        slideActiveClass: 'is-slide-active',
        slideVisibleClass: 'is-slide-visible',
        slideNextClass: 'is-slide-next',
        slidePrevClass: 'is-slide-prev',
        slidesPerView: this.num('perViewMobile', 2.2),
        spaceBetween: this.num('gapMobile', 10),
        speed: 450,
        threshold: 6,
        watchOverflow: true,
        watchSlidesProgress: true,
        grabCursor: true,
        breakpoints: {
          750: {
            slidesPerView: this.num('perViewTablet', 2.5),
            spaceBetween: this.num('gapDesktop', 24),
          },
          990: {
            slidesPerView: this.num('perViewDesktop', 4),
            spaceBetween: this.num('gapDesktop', 24),
          },
        },
        navigation: {
          prevEl: this.querySelector('[data-rps-prev]'),
          nextEl: this.querySelector('[data-rps-next]'),
          disabledClass: 'is-disabled',
          lockClass: 'is-locked',
        },
        pagination:
          paginationEl && pagination !== 'none'
            ? {
                el: paginationEl,
                type: pagination === 'dots' ? 'bullets' : 'progressbar',
                clickable: true,
                bulletElement: 'button',
                bulletClass: 'related-slider__dot',
                bulletActiveClass: 'is-active',
                progressbarFillClass: 'related-slider__progress-fill',
                modifierClass: 'related-slider__pagination--',
                lockClass: 'is-locked',
                horizontalClass: 'related-slider__pagination--horizontal',
              }
            : false,
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: {
          enabled: true,
          prevSlideMessage: this.dataset.prevLabel,
          nextSlideMessage: this.dataset.nextLabel,
          notificationClass: 'related-slider__notification',
          slideRole: 'group',
        },
      });
    }
  }

  customElements.define('related-products-slider', RelatedProductsSlider);
})();
